import React, { useEffect, useMemo, useRef, useState } from 'react';

export type SearchableSelectOption = {
  readonly value: string;
  readonly label: string;
  readonly emoji?: string;
};

interface SearchableSelectProps {
  readonly options: ReadonlyArray<SearchableSelectOption>;
  readonly value?: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly searchPlaceholder?: string;
  readonly noMatchesLabel?: string;
  readonly className?: string;
  readonly menuClassName?: string;
  readonly inputClassName?: string;
  readonly optionClassName?: string;
  readonly selectedOptionClassName?: string;
  readonly disabled?: boolean;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search',
  noMatchesLabel = 'No matches',
  className,
  menuClassName,
  inputClassName,
  optionClassName,
  selectedOptionClassName,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (target && containerRef.current && !containerRef.current.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
  }, [open]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) => {
      const haystack = `${option.label} ${option.value} ${option.emoji ?? ''}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [options, search]);

  const selected = options.find((option) => option.value === value);

  const handleSelect = (nextValue: string) => {
    onChange(nextValue);
    setOpen(false);
    setSearch('');
  };

  return (
    <div className={`relative ${open ? 'z-50' : ''}`} ref={containerRef}>
      <button
        type="button"
        className={className}
        onClick={(event) => { event.stopPropagation(); setOpen((prev) => !prev); }}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
      >
        <span className="truncate">
          {selected
            ? `${selected.emoji ? `${selected.emoji} ` : ''}${selected.label}`
            : placeholder}
        </span>
        <span className="ml-3 text-gray-400">v</span>
      </button>
      {open && (
        <div className={menuClassName} onMouseDown={(event) => event.stopPropagation()} onClick={(event) => event.stopPropagation()}>
          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
            <input
              ref={inputRef}
              className={inputClassName}
              placeholder={searchPlaceholder}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              disabled={disabled}
            />
          </div>
          <ul className="max-h-60 overflow-auto py-1" role="listbox">
            {filtered.length === 0 && (
              <li key="no-matches" className="px-3 py-2 text-sm text-gray-500">
                {noMatchesLabel}
              </li>
            )}
            {filtered.map((option, index) => {
              const isSelected = option.value === value;
              const optionClasses = isSelected ? selectedOptionClassName : optionClassName;
              return (
                <li key={`${option.value}-${index}`} role="option" aria-selected={isSelected}>
                  <button
                    type="button"
                    className={optionClasses}
                    onClick={() => handleSelect(option.value)}
                  >
                    {option.emoji ? `${option.emoji} ` : ''}
                    {option.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
